import React, { useMemo, useState, useEffect } from "react";
import { Box, Collapse, Stack, TextField, Typography, Paper, Divider, InputAdornment, Chip, Button } from "@mui/material";
import { useCalculateDutiesQuery, DutyCalculationInput, LineType } from "@/services/product-catalogue";
import { CountrySelect } from "@/pages/COMMON/product-catalogue/components/product-select-fields";
import { useLazyCheckMetalPercentageRequiredQuery } from "@/services/product-catalogue/metal-percentage-api";
import CreateProductForm from "@/pages/COMMON/product-catalogue/components/create-product-form";
import { useProductAnalysisPolling } from "@/hooks/useProductAnalysisPolling";
import { useGetProductsWithTariffsQuery } from "@/services/product-catalogue";
import DatePicker from "@/components/date-picker";
import { useGetDutyColumnsQuery, DutyColumn } from "@/services/product-catalogue/duty-columns-api";
import { Star as StarIcon, ExpandMore as ExpandMoreIcon, CalculateOutlined as CalculateIcon } from "@mui/icons-material";
import { getAuthorityStyle } from "@/pages/COMMON/product-catalogue/components/rule-form-section/helpers";

export interface InlineDutyCalculatorState {
  mainExpanded: boolean;
  detailsExpanded: boolean;
  countryOfOrigin: string;
  metalPercentage: string;
  showMetalPercentage: boolean;
  effectiveDate: string;
}

interface InlineDutyCalculatorProps {
  htsCode?: string | null;
  defaultOpen?: boolean;
  displayCode?: string; // Pretty code string to display explicitly
  initialState?: Partial<InlineDutyCalculatorState>;
  onStateChange?: (state: InlineDutyCalculatorState) => void;
}

const InlineDutyCalculator: React.FC<InlineDutyCalculatorProps> = ({ htsCode, defaultOpen = true, displayCode, initialState, onStateChange }) => {
  // Main expansion (Form + Result Summary) - default to true as requested "pre expanded"
  const [mainExpanded, setMainExpanded] = useState(initialState?.mainExpanded ?? defaultOpen);
  // Details expansion (Breakdown) - default to false
  const [detailsExpanded, setDetailsExpanded] = useState(initialState?.detailsExpanded ?? false);

  const [countryOfOrigin, setCountryOfOrigin] = useState(initialState?.countryOfOrigin ?? "");
  const [metalPercentage, setMetalPercentage] = useState<string>(initialState?.metalPercentage ?? "");
  const [showMetalPercentage, setShowMetalPercentage] = useState(initialState?.showMetalPercentage ?? false);
  const [checkMetalRequired, { data: metalReqData }] = useLazyCheckMetalPercentageRequiredQuery();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>(() => {
    if (initialState?.effectiveDate) return initialState.effectiveDate;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  });

  // Notify parent of state changes for persistence
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        mainExpanded,
        detailsExpanded,
        countryOfOrigin,
        metalPercentage,
        showMetalPercentage,
        effectiveDate
      });
    }
  }, [mainExpanded, detailsExpanded, countryOfOrigin, metalPercentage, showMetalPercentage, effectiveDate, onStateChange]);

  // Lightweight hook setup to trigger risk analysis polling after creation
  const { refetch } = useGetProductsWithTariffsQuery({ effectiveDate }, { skip: true });
  const { addProductPolling } = useProductAnalysisPolling({ queryParams: { effectiveDate }, enabled: false } as any);

  const isTenDigit = useMemo(() => Boolean(htsCode && /^\d{10}$/.test(htsCode.replace(/\D/g, ""))), [htsCode]);

  useEffect(() => {
    if (!isTenDigit) setMainExpanded(false);
  }, [isTenDigit]);

  useEffect(() => {
    if (isTenDigit && htsCode && countryOfOrigin) {
      checkMetalRequired({ htsCode, countryOfOrigin });
    } else {
      setShowMetalPercentage(false);
    }
  }, [isTenDigit, htsCode, countryOfOrigin, checkMetalRequired]);

  useEffect(() => {
    const required = Boolean((metalReqData as any)?.isMetalPercentageRequired);
    setShowMetalPercentage(required);
    if (required && !metalPercentage) {
      setMetalPercentage("100");
    }
  }, [metalReqData]);

  const input: DutyCalculationInput | null = useMemo(() => {
    if (!isTenDigit || !htsCode || !countryOfOrigin) return null;
    return {
      htsCode: htsCode,
      countryOfOrigin,
      effectiveDate,
      metalPercentage: metalPercentage ? Number(metalPercentage) : undefined,
    };
  }, [isTenDigit, htsCode, countryOfOrigin, metalPercentage, effectiveDate]);

  const { data, isFetching } = useCalculateDutiesQuery(input!, { skip: !input });
  const dutyColumnsResp = useGetDutyColumnsQuery({ pageNo: 1, pageSize: 500 } as any, { skip: !input });
  const dutyColumns: DutyColumn[] = dutyColumnsResp.data?.data || [];
  const columnMap = useMemo(() => {
    const m = new Map<string, DutyColumn>();
    dutyColumns.forEach((dc) => dc && (dc as any)?._id && m.set((dc as any)._id, dc));
    return m;
  }, [dutyColumns]);

  const footnoteColumns = useMemo(() => {
    const used = new Set<string>();
    ((data as any)?.breakdownLines || []).forEach((line: any) => {
      (line.calculationBreakdown || []).forEach((calc: any) => {
        if (
          calc?.applied &&
          typeof calc?.columnId === "string" &&
          calc.columnId !== "general-duty" &&
          calc.columnId !== "secondary-duty"
        ) {
          used.add(calc.columnId as string);
        }
      });
    });
    return Array.from(used)
      .map((id) => columnMap.get(id))
      .filter((dc): dc is DutyColumn => !!dc && Array.isArray((dc as any).citations) && (dc as any).citations.length > 0);
  }, [data, columnMap]);

  if (!isTenDigit) return null;

  return (
    <Paper 
      className="inline-duty-calculator-root"
      elevation={0} 
      sx={{ 
        border: "1px solid #EBEBEB", 
        borderRadius: "8px", 
        backgroundColor: "#F7F8F8", 
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box
        onClick={() => setMainExpanded((x) => !x)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
          transition: 'background-color 0.2s ease'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ 
                width: 20, 
                height: 20, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#000' 
            }}>
               <CalculateIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="body2" fontSize={14} fontWeight={500} color="#030712" fontFamily="Inter Display, sans-serif">
            Duty Calculator
            </Typography>
        </Stack>
        
        <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontSize={12} fontWeight={500} color="#687071" fontFamily="Inter Display, sans-serif">
            {mainExpanded ? "Hide" : "Show"}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 16, color: '#687071', transform: mainExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </Stack>
      </Box>

      {/* Main Expanded Content */}
      <Collapse in={mainExpanded}>
        <Box sx={{ px: 2, pb: 2 }}>
           {/* White Inputs Box */}
           <Paper elevation={0} sx={{ 
               p: 1.5, 
               backgroundColor: "#FFFFFF", 
               borderRadius: '6px',
               boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px -1px rgba(16, 24, 40, 0.1)'
           }}>
              <Stack direction="row" spacing={3} alignItems="flex-end" flexWrap="wrap" useFlexGap sx={{ rowGap: 2 }}>
                  {/* HTS Code Column */}
                  <Box>
                      <Typography variant="body2" fontSize={14} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                          HTS Used :
                      </Typography>
                      <Typography variant="body1" fontSize={16} fontWeight={700} color="#33404D" fontFamily="Inter Display, sans-serif" sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
                          {displayCode || htsCode}
                      </Typography>
                  </Box>

                  {/* Country Column */}
                  <Box sx={{ width: 230 }}>
                      <Typography variant="body2" fontSize={14} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                          Country of Origin
                      </Typography>
                      <Box sx={{ '& .MuiOutlinedInput-root': { height: 40, paddingRight: '30px !important' }, '& .MuiAutocomplete-input': { padding: '0px 4px !important' } }}>
                        <CountrySelect
                            label=""
                            helperText=""
                            value={countryOfOrigin}
                            onChange={(val: string) => setCountryOfOrigin(val)}
                            countries={[]}
                        />
                      </Box>
                  </Box>

                  {/* Date Column */}
                  <Box sx={{ width: 230 }}>
                      <Typography variant="body2" fontSize={14} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                          Date
                      </Typography>
                      <Box sx={{ height: 40, '& .MuiOutlinedInput-root': { height: 40 } }}>
                        <DatePicker value={effectiveDate} onChangeValue={(d) => setEffectiveDate(d ?? effectiveDate)} />
                      </Box>
                  </Box>

                  {/* Metal % Column */}
                  {showMetalPercentage && (
                      <Box sx={{ width: 120 }}>
                        <Typography variant="body2" fontSize={14} color="#AEB3B3" fontFamily="Inter Display, sans-serif" sx={{ mb: 1 }}>
                            Metal %
                        </Typography>
                        <TextField
                            value={metalPercentage}
                            onChange={(e) => setMetalPercentage(e.target.value)}
                            placeholder="100"
                            size="small"
                            fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px', height: 40 } }}
                        />
                      </Box>
                  )}

                  {/* Button Column */}
                  <Box>
                      <Button
                        variant="contained"
                        onClick={() => setIsCreateOpen(true)}
                        disabled={!htsCode || !countryOfOrigin}
                        sx={{ 
                            textTransform: 'none', 
                            background: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), #0B494B",
                            border: "1px solid #FFFFFF",
                            boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
                            borderRadius: '8px',
                            fontFamily: 'Inter Display, sans-serif',
                            fontWeight: 500,
                            height: 36,
                            px: 2,
                            whiteSpace: 'nowrap',
                            mb: '2px',
                            color: (!htsCode || !countryOfOrigin) ? undefined : '#FFFFFF',
                            '&:hover': {
                              background: "#093b3d",
                              boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                                boxShadow: 'none',
                                background: 'rgba(0, 0, 0, 0.12)',
                                border: '1px solid transparent',
                            }
                        }}
                      >
                        Create Product
                      </Button>
                  </Box>
              </Stack>
           </Paper>
           
           {/* Result Summary & Details Toggle */}
           {(data || isFetching) && (
              <Box sx={{ mt: 3 }}>
                 {/* Result Row */}
                 <Stack direction="row" alignItems="center" justifyContent="space-between">
                     <Stack direction="row" alignItems="center" spacing={1}>
                        {/* Icon could go here if needed, e.g. calculate icon */}
                        <Typography variant="body2" fontSize={14} color="#33404D" fontFamily="Inter Display, sans-serif" fontWeight={500}>
                             Total Duty Rate:
                        </Typography>
                        <Typography variant="body1" fontSize={14} color="#33404D" fontFamily="Inter Display, sans-serif" fontWeight={700}>
                             {isFetching ? "..." : `${((data?.totalDutyRate ?? 0) * 100).toFixed(2)}%`}
                        </Typography>
                     </Stack>

                     {/* Details Toggle Button */}
                     <Button 
                        onClick={() => setDetailsExpanded(prev => !prev)}
                        endIcon={<ExpandMoreIcon sx={{ transform: detailsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                        sx={{ 
                            textTransform: 'none', 
                            color: '#687071', 
                            fontWeight: 500,
                            fontSize: 12,
                            fontFamily: 'Inter Display, sans-serif',
                            '&:hover': { bgcolor: 'transparent', color: '#33404D' }
                        }}
                     >
                        {detailsExpanded ? "Hide Details" : "View Details"}
                     </Button>
                 </Stack>

                 {/* Detailed Breakdown */}
                 <Collapse in={detailsExpanded}>
                    <Box sx={{ mt: 2 }}>
                        {((data as any)?.secondaryDutyRateString || '').toString().trim().toLowerCase() !== 'null' &&
                            (data as any)?.secondaryDutyRateString && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: '#F3F4F6', borderRadius: '6px' }}>
                            <Typography variant="caption" color="text.secondary" fontFamily="Inter, sans-serif">
                                Specific Duty: <span style={{ fontWeight: 600, color: '#374151' }}>{(data as any).secondaryDutyRateString}</span>
                            </Typography>
                            </Box>
                        )}
                        
                        {(data as any)?.ftaApplicable && (
                            <Box sx={{ mt: 2 }}>
                            <Chip 
                                color="success" 
                                label={`Preferential Origin: ${(data as any)?.ftaProgramName || 'Applicable'} (Duty Exempt)`} 
                                size="small" 
                                sx={{ borderRadius: '4px', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
                            />
                            </Box>
                        )}

                        {!!data && (Array.isArray((data as any).breakdownLines) && (data as any).breakdownLines.length > 0 ? (
                            <Stack spacing={1.5} sx={{ mt: 3 }}>
                            {(data as any).breakdownLines.map((line: any, idx: number) => (
                                <Box key={idx} sx={{ border: "1px solid #F3F4F6", borderRadius: "8px", p: 1.5, bgcolor: '#FAFAFA' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={line.lineType === LineType.METAL ? 'Metal' : line.lineType === LineType.NONMETAL ? 'Non-Metal' : 'Full Value'}
                                        size="small"
                                        sx={{
                                        backgroundColor: line.lineType === LineType.METAL ? '#FFF7ED' : line.lineType === LineType.NONMETAL ? '#EFF6FF' : '#ECFDF5',
                                        color: line.lineType === LineType.METAL ? '#C2410C' : line.lineType === LineType.NONMETAL ? '#1D4ED8' : '#047857',
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                        border: '1px solid transparent',
                                        borderColor: line.lineType === LineType.METAL ? '#FFEDD5' : line.lineType === LineType.NONMETAL ? '#DBEAFE' : '#D1FAE5',
                                        }}
                                    />
                                    {line.portion < 1 && (
                                        <Typography variant="caption" color="text.secondary" fontFamily="Inter, sans-serif">({(line.portion * 100).toFixed(0)}% of value)</Typography>
                                    )}
                                    </Stack>
                                    <Typography variant="body2" fontWeight={600} fontFamily="Inter, sans-serif">{(line.totalRate * 100).toFixed(2)}%</Typography>
                                </Stack>
                                <Divider sx={{ my: 1, borderColor: '#E5E7EB' }} />
                                {line.applicableRules?.length ? (
                                    <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5, display: 'block' }}>APPLIED RULES</Typography>
                                    <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                                        {line.applicableRules.map((rule: any, ridx: number) => {
                                        const excluded = (line.exclusionsApplied || []).some((e: any) => e.columnId === rule.columnId);
                                        const color = excluded
                                            ? getAuthorityStyle((line.exclusionsApplied || []).find((e: any) => e.columnId === rule.columnId)?.authority).color
                                            : undefined;
                                        if (rule.columnId === 'secondary-duty') {
                                            const s = ((data as any)?.secondaryDutyRateString || '').toString().trim();
                                            if (!s || s.toLowerCase() === 'null') {
                                            return null;
                                            }
                                            return (
                                            <li key={ridx}>
                                                <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">Specific Duty: {s}</Typography>
                                            </li>
                                            );
                                        }
                                        return (
                                        <li key={ridx}>
                                            <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">
                                            {rule.columnName}: {(rule.rate * 100).toFixed(2)}%
                                            {excluded && (
                                                <>
                                                <StarIcon sx={{ fontSize: 14, color, verticalAlign: 'middle', ml: 0.5 }} />
                                                <span style={{ marginLeft: 4, color: '#059669', fontWeight: 500 }}>{"→ 0%"}</span>
                                                </>
                                            )}
                                            </Typography>
                                        </li>
                                        );
                                        })}
                                    </Box>
                                    </Box>
                                ) : (
                                    <Typography variant="caption" color="text.secondary" fontStyle="italic">No applicable duties on this portion.</Typography>
                                )}
                                </Box>
                            ))}
                            </Stack>
                        ) : null)}

                        {/* Citations */}
                        {footnoteColumns.length > 0 && (
                            <Paper elevation={0} sx={{ mt: 2, p: 1.5, border: "1px solid #E5E7EB", borderRadius: '8px', bgcolor: '#F9FAFB' }}>
                            <Typography variant="subtitle2" fontSize={12} fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase', color: '#4B5563' }}>Sources</Typography>
                            <Stack spacing={1.5}>
                                {footnoteColumns.map((dc: any, idx: number) => (
                                <Box key={dc._id}>
                                    <Typography variant="body2" fontWeight={600} fontSize={13} color="#111827">
                                    {idx + 1}. {dc.name}
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                                    {(dc.citations || []).map((c: any, cidx: number) => (
                                        <li key={cidx}>
                                            <Typography variant="body2" fontSize={13} sx={{ wordBreak: 'break-all' }}>
                                            <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0B494B', textDecoration: 'none' }}>{c.url}</a>
                                            </Typography>
                                        </li>
                                    ))}
                                    </Box>
                                </Box>
                                ))}
                            </Stack>
                            </Paper>
                        )}
                    </Box>
                 </Collapse>
              </Box>
           )}
        </Box>
      </Collapse>

      <CreateProductForm
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onProductCreated={(created) => {
          setIsCreateOpen(false);
          try {
            if (created && addProductPolling) {
              addProductPolling(created, 'creation');
            }
          } catch {}
          try {
            refetch && refetch();
          } catch {}
        }}
        initialData={{
          htsCode: (displayCode ?? htsCode ?? undefined) as string | undefined,
          countryOfOrigin,
          metalPercentage: showMetalPercentage && metalPercentage ? Number(metalPercentage) : undefined,
        }}
      />
    </Paper>
  );
};

export default InlineDutyCalculator;